/* eslint-disable react/prop-types */
/**
 * TyrePortalCard
 * ==============
 * Iter187 — Customer-facing portal card for the structured tyre
 * replacement group with PER-TYRE approve/decline toggles.
 *
 * UX (iter189):
 *  • Each tyre child shows: severity badge, position, issue, wear
 *    pattern, allocated price, and an Approve/Decline switch.
 *  • Declined tyres turn grey-strikethrough; group total updates live.
 *  • Group-level approve still works — the parent reads the
 *    `declinedPositions` array and forwards it on submit.
 */
import { useEffect, useState } from "react";
import {
  AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Layers, MoveRight,
} from "lucide-react";

const SEVERITY_BADGE = {
  dangerous: "bg-red-600 text-white", major: "bg-red-500 text-white",
  damaged: "bg-orange-600 text-white", puncture: "bg-orange-500 text-white",
  advisory: "bg-amber-500 text-white", worn: "bg-amber-400 text-amber-900",
  uneven_wear: "bg-amber-300 text-amber-900", age_related: "bg-slate-400 text-white",
};
const SEVERITY_LABELS = {
  dangerous: "Dangerous", major: "Major", damaged: "Damaged",
  puncture: "Puncture", advisory: "Advisory", worn: "Worn",
  uneven_wear: "Uneven Wear", age_related: "Age-Related",
};

/* iter211ai — Reason badges.
   ---------------------------------------------------------------
   Merged tyre children now carry a " · "-joined `issue` string —
   e.g. "Nearside Rear Inner Tyre worn close to legal limit/worn
   on edge (5.2.3 (e)) · Nearside Rear Nail in tyre". Rendering
   that as prose buries the "why" in DVSA formality.
   Split by " · " and map each fragment to a short, human badge
   so a customer instantly sees "Worn + Nail" instead of a wall of
   text. Unrecognised fragments fall through to a generic "Defect"
   badge so we never fail loudly.                                   */
const REASON_BADGES = [
  { key: "nail",       label: "Nail",         re: /\bnail\b/i },
  { key: "screw",      label: "Screw",        re: /\bscrew\b/i },
  { key: "puncture",   label: "Puncture",     re: /\bpunctur/i },
  { key: "cut",        label: "Cut",          re: /\bcut\b/i },
  { key: "bulge",      label: "Bulge",        re: /\bbulge\b/i },
  { key: "sidewall",   label: "Sidewall",     re: /\bsidewall\b/i },
  { key: "cord",       label: "Cord exposed", re: /\bcord/i },
  { key: "ply",        label: "Ply exposed",  re: /\bply\b/i },
  { key: "below_16",   label: "Below 1.6mm",  re: /\b1\.?6\s*mm\b|below (?:the )?(?:legal|requirement)/i },
  { key: "worn",       label: "Worn",         re: /\bworn (?:close|through)\b|worn on edge|close to legal/i },
  { key: "uneven",     label: "Uneven wear",  re: /\buneven wear\b|worn.*edge/i },
  { key: "damaged",    label: "Damaged",      re: /\bdamag/i },
  { key: "cracking",   label: "Cracking",     re: /\bcrack/i },
  { key: "perishing",  label: "Perishing",    re: /\bperish/i },
  { key: "aged",       label: "Aged rubber",  re: /\baged\b/i },
  { key: "deform",     label: "Deformed",     re: /\bdeform/i },
  { key: "pressure",   label: "Low pressure", re: /\bpressure\b/i },
];

function _classifyReason(fragment) {
  const trimmed = (fragment || "").trim();
  if (!trimmed) return null;
  for (const rb of REASON_BADGES) {
    if (rb.re.test(trimmed)) return rb;
  }
  return { key: "defect", label: "Defect" };
}

function _splitTyreReasons(issue) {
  return (issue || "")
    .split(" · ")
    .map((s) => s.trim())
    .filter(Boolean);
}


export default function TyrePortalCard({
  item, isApproved, isDangerous,
  onPerTyreDecisionChange,   // (item_id, declinedPositions, approvedPositions) => void
  forceDeclinedPositions,    // iter198p — externally-controlled decline
                             // set. Used by the "Essential safety work
                             // only" path to auto-decline non-essential
                             // tyres on selection.
  forceApprovedPositions,    // iter198u (Feb 2026) — externally-
                             // controlled APPROVE set. The parent
                             // tracks per-tyre approvals in
                             // `perTyreApproved` so that remounts
                             // (triggered by the React key including
                             // the forced lists) seed the touched +
                             // declined sets correctly. Without this
                             // prop, approving a tyre then declining
                             // a different one wiped the first
                             // approval — the touched set was being
                             // rebuilt from declines alone.
  forceTouchAll,             // iter198r — top-level options that
                             // cover every tyre (e.g. "Complete all
                             // recommended work") flip this true so
                             // the per-tyre buttons render as solid
                             // green (or red, depending on
                             // forceDeclinedPositions).
}) {
  const [expanded, setExpanded] = useState(isDangerous);
  const [declined, setDeclined] = useState(
    () => new Set(forceDeclinedPositions || []),
  );
  // iter204d — Per-tyre puncture-repair selections. When the customer
  // clicks the "£25 repair instead" CTA on an eligible tyre, that
  // position lands in this set and the displayed price + group
  // total flip to the repair price. The parent tracks the set via
  // ``onPerTyreDecisionChange`` so the submit payload carries it
  // through to FOH.
  const [repairs, setRepairs] = useState(() => new Set());
  const [touched, setTouched] = useState(() => {
    const seed = new Set();
    if (Array.isArray(forceDeclinedPositions)) {
      for (const p of forceDeclinedPositions) seed.add(p);
    }
    // iter198u — seed touched from PREVIOUSLY-APPROVED positions too
    // so remounts (driven by the parent's `key` including the forced
    // lists) preserve customer decisions made in earlier toggle clicks.
    if (Array.isArray(forceApprovedPositions)) {
      for (const p of forceApprovedPositions) seed.add(p);
    }
    if (forceTouchAll && Array.isArray(item.tyre_group_tyres)) {
      for (const t of item.tyre_group_tyres) {
        if (t.replacement_required) seed.add(t.position);
      }
    }
    return seed;
  });

  const tyres   = item.tyre_group_tyres || [];
  const summary = item.tyre_group_portal_summary || {};
  const axlePair  = item.tyre_group_axle_pair;
  const alignSug  = item.tyre_group_alignment_suggest;
  const integrity = item.tyre_group_integrity_tier;

  const immediate = tyres.filter((t) =>
    ["dangerous", "major", "damaged", "puncture"].includes(t.severity));
  const nearing   = tyres.filter((t) =>
    ["advisory", "worn", "uneven_wear", "age_related"].includes(t.severity));
  const included  = summary.included_with_replacement || [
    "Wheel balancing", "Tyre valve replacement", "Tyre disposal",
  ];

  // Notify parent whenever decline set changes — parent collects
  // these across all tyre groups and pushes them on submit.
  // iter198s (Feb 2026) — Also report the APPROVED positions
  // (touched && not declined) so the parent's getSelectedTotal can
  // include per-tyre prices in the bottom-bar "+ Additional" total
  // when the customer authorises tyres individually rather than via
  // a top-level option.
  useEffect(() => {
    const approvedPositions = (item.tyre_group_tyres || [])
      .filter(
        (t) => t.replacement_required
          && touched.has(t.position)
          && !declined.has(t.position),
      )
      .map((t) => t.position);
    onPerTyreDecisionChange?.(
      item.id,
      Array.from(declined),
      approvedPositions,
      // iter204d — 4th arg: positions the customer chose the £25
      // repair for. Older parents will ignore this; the updated
      // MobileCustomerPortal stores it under perTyreRepairs.
      Array.from(repairs),
    );
  }, [declined, touched, repairs, item.id, item.tyre_group_tyres, onPerTyreDecisionChange]);

  const wrapClass = isDangerous
    ? "bg-slate-900 border border-slate-700"
    : "bg-white border border-red-200";

  const replacementTyres = tyres.filter((t) => t.replacement_required);
  // iter198r — Per-tyre counts now distinguish three states: approved
  // (customer explicitly committed via per-tyre Approve OR a covering
  // top-level option), declined (explicit decline), and pending (no
  // decision yet). The header summary reflects this trichotomy so the
  // customer never sees "4 of 4 tyres approved" before any action.
  const approvedCount = replacementTyres.filter(
    (t) => touched.has(t.position) && !declined.has(t.position),
  ).length;
  const declinedCount = replacementTyres.filter(
    (t) => declined.has(t.position),
  ).length;
  const pendingCount =
    replacementTyres.length - approvedCount - declinedCount;
  // iter197b — when ANY in-quote tyre is still unpriced (backend marks it
  // with price_pending=true and allocated_price_inc_vat=null), we must
  // NOT show a misleading numeric total. Show "Price TBC" instead.
  const anyPricePending = replacementTyres.some(
    (t) => t.price_pending === true || t.allocated_price_inc_vat === null
  );
  // iter198t (Feb 2026) — Card header total mirrors the approved set:
  // only tyres the customer has explicitly approved (touched and not
  // currently declined) contribute. Pending tyres count as £0 here so
  // the header price matches the bottom-bar "+ Additional" total and
  // the customer never sees "£179.88" next to a "0 of 4 approved"
  // summary line.
  // iter204d — When the customer chose the £25 puncture repair for a
  // tyre, that tyre contributes its repair price to the displayed
  // total instead of the full replacement price. The repair price is
  // stamped on the child by ``ensure_puncture_repair_option``.
  const _tyrePriceForDisplay = (t) => {
    if (repairs.has(t.position)) {
      const opt = t.repair_option || {};
      // ``price`` is net; gross at 20% VAT for portal display.
      const net = Number(opt.price || 0);
      const vatRate = (opt.vat_rate ?? 20) / 100;
      return Number((net * (1 + vatRate)).toFixed(2));
    }
    return Number(t.allocated_price_inc_vat || 0);
  };
  const displayedTotal = replacementTyres
    .filter((t) => touched.has(t.position) && !declined.has(t.position))
    .reduce((s, t) => s + _tyrePriceForDisplay(t), 0);
  // iter211u — Potential total (all replacement tyres priced at their
  // allocated_price_inc_vat). Used to show "up to £X.XX" when the
  // customer hasn't started approving yet — otherwise the header
  // reads a jarring "£0.00" next to "0 of 2 tyres approved" while
  // the individual tyres show real prices, and the customer thinks
  // pricing is broken.
  const potentialTotal = replacementTyres.reduce(
    (s, t) => s + Number(t.allocated_price_inc_vat || 0),
    0,
  );
  const noneTouchedYet = approvedCount === 0 && declinedCount === 0;

  const toggleDecline = (position) => {
    setTouched((prev) => {
      const next = new Set(prev);
      next.add(position);
      return next;
    });
    setDeclined((prev) => {
      const next = new Set(prev);
      if (next.has(position)) next.delete(position);
      else next.add(position);
      return next;
    });
  };

  const approvePosition = (position) => {
    // iter198r — Mark touched + ensure not in the declined set so the
    // Approve button flips to solid green ("✓ Approved").
    setTouched((prev) => {
      const next = new Set(prev);
      next.add(position);
      return next;
    });
    setDeclined((prev) => {
      if (!prev.has(position)) return prev;
      const next = new Set(prev);
      next.delete(position);
      return next;
    });
  };

  // iter204d — Customer chose the £25 repair instead of a full
  // replacement. Marks touched + approved + flips the per-tyre
  // ``repairs`` flag. Clicking again toggles back to replacement.
  const toggleRepair = (position) => {
    setTouched((prev) => {
      const next = new Set(prev);
      next.add(position);
      return next;
    });
    setDeclined((prev) => {
      if (!prev.has(position)) return prev;
      const next = new Set(prev);
      next.delete(position);
      return next;
    });
    setRepairs((prev) => {
      const next = new Set(prev);
      if (next.has(position)) next.delete(position);
      else next.add(position);
      return next;
    });
  };

  const renderTyreRow = (t) => {
    const isDeclined = declined.has(t.position);
    // iter198r — A tyre is shown as APPROVED (solid green pill) only
    // when the customer has explicitly touched it AND not declined it.
    // Untouched tyres render with both buttons in outlined styling so
    // the portal doesn't visually pre-commit the customer's choice.
    const isApprovedTyre = touched.has(t.position) && !isDeclined;
    const pricePending = t.price_pending === true || t.allocated_price_inc_vat === null;
    const allocated = Number(t.allocated_price_inc_vat || 0);
    // iter197f — Customer-facing "why / what if not" explainers. The
    // backend stamps these on each child via _portal_consequence_copy
    // based on severity. Falls back to nothing if missing (older
    // payloads pre-upgrade).
    const portalWhy = (t.portal_why || "").trim();
    const portalIfNot = (t.portal_if_not || "").trim();
    // iter204d — Eligible for the customer-facing £25 puncture repair?
    const isRepairing = repairs.has(t.position);
    const repairOpt = t.repair_option || {};
    const repairAvailable = repairOpt.available === true;
    // iter211aj — ``non_repairable`` on the child reflects the DVSA
    // defect description. If the puncture is actually within the
    // BS AU159 safe-repair zone (backend sets ``repair_option.available``
    // to true), suppress the warning badge so we don't contradict the
    // £25 repair offer we're about to show.
    const showNonRepairable = t.non_repairable === true && !repairAvailable;
    const repairGross = repairAvailable
      ? Number((Number(repairOpt.price || 0) * (1 + (repairOpt.vat_rate ?? 20) / 100)).toFixed(2))
      : null;
    // Savings = full replacement gross - repair gross.
    const repairSavings = repairAvailable
      ? Math.max(0, Number(allocated || 0) - repairGross)
      : 0;
    return (
      <li
        key={t.position}
        className={`py-2 px-2 rounded transition-colors ${
          isDeclined
            ? (isDangerous ? "bg-slate-800/50" : "bg-slate-100")
            : (isDangerous ? "bg-slate-800/30" : "bg-white")
        }`}
        data-testid={`tyre-portal-row-${item.id}-${t.position}`}
        data-declined={isDeclined ? "true" : "false"}
      >
        <div className="flex items-start gap-2 text-[12.5px]">
          <span className={`shrink-0 text-[9.5px] uppercase tracking-wider px-1.5 py-0.5 rounded ${
            SEVERITY_BADGE[t.severity] || "bg-slate-300 text-slate-900"
          } ${isDeclined ? "opacity-50" : ""}`}>
            {SEVERITY_LABELS[t.severity] || t.severity}
          </span>
          <div className={`min-w-0 flex-1 ${isDeclined ? "line-through opacity-60" : ""}`}>
            <p className={`font-semibold ${isDangerous ? "text-slate-100" : "text-slate-900"}`}>
              {t.position_label || t.position} tyre
              {(t.tyre_size || t.tyre_brand || t.tyre_model) && (
                <span className={`ml-1.5 font-normal ${
                  isDangerous ? "text-slate-300" : "text-slate-600"
                }`}>
                  — {[t.tyre_size, t.tyre_brand, t.tyre_model].filter(Boolean).join(" ")}
                </span>
              )}
              {/* iter211aj — Non-repairable indicator. When any of
                  the tyre's reasons is inherently unrepairable
                  (nail, cut, bulge, cord, ply, sidewall) the backend
                  stamps ``non_repairable: true``. Show a compact
                  warning badge inline with the position so the
                  customer's #1 question ("can't we just repair it?")
                  is answered before they ask. */}
              {showNonRepairable && (
                <span
                  className={`ml-1.5 inline-flex items-center gap-0.5 text-[9.5px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                    isDangerous
                      ? "bg-red-500/30 text-red-100 border border-red-400/40"
                      : "bg-red-100 text-red-800 border border-red-200"
                  }`}
                  title="This damage cannot be safely repaired under British Standard AU159."
                  data-testid={`tyre-non-repairable-${item.id}-${t.position}`}
                >
                  ⚠ Non-repairable
                </span>
              )}
            </p>
            <p className={isDangerous ? "text-slate-300" : "text-slate-700"} data-testid={`tyre-portal-issue-${item.id}-${t.position}`}>
              {t.issue}
            </p>
            {/* iter211ai — Reason badges: distil the merged issue
                text ("Worn + Nail") into scannable pills. */}
            {(() => {
              const _reasons = _splitTyreReasons(t.issue)
                .map(_classifyReason)
                .filter(Boolean);
              // De-dupe by key while preserving order so "worn ·
              // worn on edge" doesn't render two Worn pills.
              const _seen = new Set();
              const _uniq = _reasons.filter((r) => {
                if (_seen.has(r.key)) return false;
                _seen.add(r.key);
                return true;
              });
              if (_uniq.length <= 1) return null;
              return (
                <div
                  className="mt-1.5 flex flex-wrap gap-1"
                  data-testid={`tyre-portal-reason-badges-${item.id}-${t.position}`}
                >
                  {_uniq.map((r) => (
                    <span
                      key={r.key}
                      className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${
                        isDangerous
                          ? "bg-red-900/60 text-red-100 border border-red-500/40"
                          : "bg-red-100 text-red-800 border border-red-200"
                      }`}
                      data-testid={`tyre-reason-badge-${r.key}-${t.position}`}
                    >
                      {r.label}
                    </span>
                  ))}
                </div>
              );
            })()}
            {t.wear_pattern && (
              <p className={`italic text-[11px] ${
                isDangerous ? "text-slate-400" : "text-slate-500"
              }`}>
                Wear pattern: {t.wear_pattern.replace(/_/g, " ")}
              </p>
            )}
          </div>
          <div className="text-right shrink-0 ml-2">
            <p className={`font-bold ${
              isDangerous ? "text-white" : "text-slate-900"
            } ${isDeclined ? "line-through opacity-60" : ""}`}
              data-testid={`tyre-portal-price-${item.id}-${t.position}`}
              data-price-pending={pricePending ? "true" : "false"}
              data-repairing={isRepairing ? "true" : "false"}
            >
              {pricePending ? (
                <span className={`text-[10.5px] uppercase tracking-wider font-black px-1.5 py-0.5 rounded ${
                  isDangerous ? "bg-amber-500 text-slate-900" : "bg-amber-400 text-amber-900"
                }`}>
                  Price&nbsp;TBC
                </span>
              ) : isRepairing ? (
                <>
                  <span className={`text-[10.5px] line-through ${isDangerous ? "text-slate-400" : "text-slate-400"} mr-1`}>
                    £{allocated.toFixed(2)}
                  </span>
                  <span className="text-emerald-600">£{repairGross.toFixed(2)}</span>
                </>
              ) : (
                <>£{allocated.toFixed(2)}</>
              )}
            </p>
            <div className="flex items-center gap-2 mt-1.5">
              {/* iter198n (Feb 2026) — Split single-toggle into an
                  explicit Approve | Decline pair so the customer has
                  to actively commit per-tyre.
                  iter198r (Feb 2026) — Buttons render in OUTLINED
                  styling by default; they only flip to solid green
                  (Approve) / solid red (Decline) AFTER the customer
                  has touched the tyre (either by clicking one of the
                  pair or via a top-level option like "Complete all
                  recommended work" / "Essential safety only" that
                  the parent represents through `forceDeclinedPositions`
                  and `forceTouchAll`). This stops the portal from
                  visually pre-committing the customer's decision.
                  iter211at (Feb 2026) — Sized the primary Approve
                  button up considerably (from text-[10.5px]/px-2 py-0.5
                  → text-sm/px-4 py-2) so it reads as the main tap
                  target on the customer portal tyre card. Approve is
                  now noticeably wider (`flex-1`) and taller than
                  Decline; Decline stays smaller as it's the secondary
                  path. */}
              <button
                type="button"
                onClick={() => approvePosition(t.position)}
                className={`flex-1 text-sm uppercase tracking-wider px-4 py-2 rounded-lg border-2 font-bold transition-all ${
                  isApprovedTyre
                    ? "bg-emerald-500 text-white border-emerald-500 shadow-md"
                    : "bg-white text-emerald-700 border-emerald-400 hover:bg-emerald-50 shadow-sm"
                }`}
                data-testid={`tyre-portal-approve-${item.id}-${t.position}`}
                aria-pressed={isApprovedTyre}
              >
                {isApprovedTyre ? "✓ Approved" : "Approve"}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!isDeclined) {
                    toggleDecline(t.position);
                  }
                }}
                className={`text-[11px] uppercase tracking-wider px-2.5 py-1.5 rounded border-2 font-semibold transition-all ${
                  isDeclined
                    ? "bg-red-500 text-white border-red-500 shadow-sm"
                    : "bg-white text-red-700 border-red-200 hover:bg-red-50 hover:border-red-300"
                }`}
                data-testid={`tyre-portal-toggle-${item.id}-${t.position}`}
                aria-pressed={isDeclined}
              >
                {isDeclined ? "✓ Declined" : "Decline"}
              </button>
            </div>
            {/* iter204d — Customer-facing "£25 puncture repair instead"
                CTA. Only renders when the backend stamped a
                ``repair_option`` on the child (nail / screw / foreign
                object in the tread + no sidewall / cord damage). The
                button is small but distinctive so the customer notices
                the cheaper alternative without feeling up-sold. */}
            {repairAvailable && !isDeclined && (
              <button
                type="button"
                onClick={() => toggleRepair(t.position)}
                className={`mt-1.5 w-full text-[10.5px] uppercase tracking-wider px-2 py-1 rounded border-2 font-semibold transition-all flex items-center justify-center gap-1 ${
                  isRepairing
                    ? "bg-amber-500 text-white border-amber-500 shadow-sm"
                    : "bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100 hover:border-amber-400"
                }`}
                data-testid={`tyre-portal-repair-${item.id}-${t.position}`}
                aria-pressed={isRepairing}
                title={isRepairing
                  ? "Switch back to a full tyre replacement."
                  : `Workshop can plug the puncture for £${repairGross.toFixed(2)} — saves you £${repairSavings.toFixed(2)}.`}
              >
                <span aria-hidden>🔧</span>
                {isRepairing ? "Repair £" + repairGross.toFixed(2) : `Try £${repairGross.toFixed(2)} repair?`}
                {!isRepairing && repairSavings >= 1 && (
                  <span className="ml-1 px-1 py-0 rounded bg-emerald-100 text-emerald-700 text-[9px] font-black border border-emerald-300">
                    Save £{repairSavings.toFixed(0)}
                  </span>
                )}
              </button>
            )}
            {t.severity === "dangerous" && isDeclined && (
              <p className={`text-[10px] mt-1 leading-tight ${
                isDangerous ? "text-red-300" : "text-red-600"
              }`}>
                ⚠ Declining a<br/>dangerous tyre
              </p>
            )}
          </div>
        </div>
        {/* iter197f — Why this tyre needs replacement + risk if
            not repaired. Severity-aware copy from the backend.
            iter199 (Feb 2026) — Moved OUT of the inner flex row so
            it spans the full card width on mobile instead of being
            squished beside the price/approve/decline column. */}
        {(portalWhy || portalIfNot) && !isDeclined && (
          <div
            className={`mt-2 rounded-md border px-2.5 py-2 text-[11.5px] leading-snug space-y-1.5 ${
              isDangerous
                ? "bg-red-950/40 border-red-700/50"
                : (t.severity === "advisory" || t.severity === "worn"
                    ? "bg-amber-50 border-amber-200"
                    : "bg-slate-50 border-slate-200")
            }`}
            data-testid={`tyre-portal-explainer-${item.id}-${t.position}`}
          >
            {portalWhy && (
              <p className={isDangerous ? "text-slate-200" : "text-slate-700"}>
                <span className={`font-bold uppercase tracking-wider text-[10px] block mb-0.5 ${
                  isDangerous ? "text-red-300" : "text-slate-500"
                }`}>
                  Why this needs replacing
                </span>
                {portalWhy}
              </p>
            )}
            {portalIfNot && (
              <p className={isDangerous ? "text-yellow-100" : "text-slate-700"}>
                <span className={`font-bold uppercase tracking-wider text-[10px] block mb-0.5 ${
                  isDangerous ? "text-yellow-300" : "text-slate-500"
                }`}>
                  If not replaced
                </span>
                {portalIfNot}
              </p>
            )}
          </div>
        )}
      </li>
    );
  };

  return (
    <div
      className={`rounded-xl overflow-hidden transition-all ${wrapClass}`}
      data-testid={`tyre-portal-card-${item.id}`}
      data-tyre-count={tyres.length}
      data-integrity-tier={integrity}
      data-declined-count={declinedCount}
    >
      {/* Header */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className={`w-full p-3 flex items-center justify-between hover:opacity-90 ${
          isDangerous ? "hover:bg-slate-800" : "hover:bg-red-50"
        }`}
        data-testid={`tyre-portal-card-toggle-${item.id}`}
      >
        <div className="flex-1 text-left min-w-0">
          {immediate.length > 0 && (
            <div
              className="inline-flex items-center gap-1.5 bg-yellow-400 text-red-800 px-2 py-0.5 rounded mb-1.5 ring-2 ring-yellow-200"
              data-testid={`tyre-portal-card-urgency-${item.id}`}
            >
              <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2.5} />
              <span className="text-[10px] font-black uppercase tracking-wider">
                {immediate.length} Immediate Safety Issue{immediate.length === 1 ? "" : "s"}
              </span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Layers className={`w-4 h-4 ${isDangerous ? "text-slate-400" : "text-slate-500"}`} />
            <span className={`font-semibold text-sm ${isDangerous ? "text-white" : "text-slate-900"}`}>
              {summary.headline || "Tyre Safety & Wear Issues Identified"}
            </span>
            {isApproved && <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />}
          </div>
          <p className={`text-[11.5px] mt-1 ${isDangerous ? "text-slate-400" : "text-slate-500"}`}>
            {approvedCount} of {replacementTyres.length} tyres approved
            {declinedCount > 0 && ` · ${declinedCount} declined`}
            {pendingCount > 0 && ` · ${pendingCount} pending`}
            {immediate.length > 0 && ` · ${immediate.length} need immediate attention`}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`font-bold text-sm ${isDangerous ? "text-white" : "text-red-700"}`}
            data-testid={`tyre-portal-card-total-${item.id}`}
            data-price-pending={anyPricePending ? "true" : "false"}
          >
            {anyPricePending ? (
              <span className="text-[10px] uppercase tracking-wider font-black bg-amber-400 text-amber-900 px-1.5 py-0.5 rounded">
                Price&nbsp;TBC
              </span>
            ) : noneTouchedYet && potentialTotal > 0 ? (
              // iter211u — Before the customer has approved anything,
              // show the "up to £X" price so the header matches what
              // the individual tyre rows are showing. Once they start
              // approving/declining, the header transitions to the
              // running approved total (design intent from iter198t).
              <span className="flex flex-col items-end leading-tight" data-testid={`tyre-portal-card-potential-${item.id}`}>
                <span className="text-[9.5px] uppercase tracking-wider font-semibold text-slate-500">up to</span>
                <span>£{potentialTotal.toFixed(2)}</span>
              </span>
            ) : (
              <>£{displayedTotal.toFixed(2)}</>
            )}
          </span>
          {expanded ? (
            <ChevronDown className={`w-4 h-4 ${isDangerous ? "text-slate-400" : "text-red-500"}`} />
          ) : (
            <ChevronRight className={`w-4 h-4 ${isDangerous ? "text-slate-400" : "text-red-500"}`} />
          )}
        </div>
      </button>

      {expanded && (
        <div className={`px-3 pb-3 pt-2 border-t space-y-3 ${
          isDangerous ? "border-slate-700 bg-slate-800" : "border-red-100 bg-red-50/30"
        }`}>
          {/* Immediate Safety */}
          {immediate.length > 0 && (
            <section data-testid={`tyre-portal-immediate-${item.id}`}>
              <p className={`text-[11px] font-bold uppercase tracking-wider mb-1.5 ${
                isDangerous ? "text-red-300" : "text-red-700"
              }`}>
                Immediate Safety Issues
              </p>
              <ul className="space-y-1.5">
                {immediate.map(renderTyreRow)}
              </ul>
            </section>
          )}

          {/* Nearing Legal Limit */}
          {nearing.length > 0 && (
            <section data-testid={`tyre-portal-nearing-${item.id}`}>
              <p className={`text-[11px] font-bold uppercase tracking-wider mb-1.5 ${
                isDangerous ? "text-amber-300" : "text-amber-700"
              }`}>
                Tyres Nearing Legal Limit
              </p>
              <ul className="space-y-1.5">
                {nearing.map(renderTyreRow)}
              </ul>
            </section>
          )}

          {/* Included With Replacement */}
          {included.length > 0 && (
            <section
              className={`rounded-lg p-2.5 ${
                isDangerous ? "bg-emerald-900/30 border border-emerald-700/50" : "bg-emerald-50 border border-emerald-200"
              }`}
              data-testid={`tyre-portal-included-${item.id}`}
            >
              <p className={`text-[11px] font-bold uppercase tracking-wider mb-1.5 ${
                isDangerous ? "text-emerald-300" : "text-emerald-700"
              }`}>
                Included with each approved tyre
              </p>
              <div className="flex flex-wrap gap-1.5">
                {included.map((it) => (
                  <span
                    key={it}
                    className={`text-[11.5px] rounded px-2 py-0.5 flex items-center gap-1 ${
                      isDangerous
                        ? "bg-emerald-950 text-emerald-200 border border-emerald-700"
                        : "bg-white text-emerald-800 border border-emerald-300"
                    }`}
                  >
                    <CheckCircle2 className="w-3 h-3" /> {it}
                  </span>
                ))}
              </div>
            </section>
          )}

          {/* Recommended Additional Service */}
          {(alignSug?.warranted || axlePair) && (
            <section
              className={`rounded-lg p-2.5 space-y-2 ${
                isDangerous ? "bg-blue-900/30 border border-blue-700/50" : "bg-blue-50 border border-blue-200"
              }`}
              data-testid={`tyre-portal-recommended-${item.id}`}
            >
              <p className={`text-[11px] font-bold uppercase tracking-wider ${
                isDangerous ? "text-blue-300" : "text-blue-700"
              }`}>
                Recommended Additional Service
              </p>
              {alignSug?.warranted && (
                <div
                  className="flex items-start gap-2 text-[12.5px]"
                  data-testid={`tyre-portal-alignment-${item.id}`}
                >
                  <MoveRight className={`w-4 h-4 mt-0.5 shrink-0 ${
                    isDangerous ? "text-blue-300" : "text-blue-700"
                  }`} />
                  <div className="min-w-0">
                    <p className={`font-semibold ${isDangerous ? "text-slate-100" : "text-blue-900"}`}>
                      Wheel alignment recommended
                    </p>
                    <p className={`italic text-[11.5px] leading-snug ${
                      isDangerous ? "text-slate-300" : "text-slate-700"
                    }`}>
                      {alignSug.reason}
                    </p>
                  </div>
                </div>
              )}
              {axlePair && (
                <div
                  className="flex items-start gap-2 text-[12.5px]"
                  data-testid={`tyre-portal-axle-pair-${item.id}`}
                >
                  <AlertTriangle className={`w-4 h-4 mt-0.5 shrink-0 ${
                    isDangerous ? "text-amber-300" : "text-amber-700"
                  }`} />
                  <div className="min-w-0">
                    <p className={`font-semibold ${isDangerous ? "text-slate-100" : "text-slate-900"}`}>
                      {axlePair.label}
                    </p>
                    <p className={`italic text-[11.5px] leading-snug ${
                      isDangerous ? "text-slate-300" : "text-slate-700"
                    }`}>
                      {axlePair.rationale}
                    </p>
                  </div>
                </div>
              )}
            </section>
          )}
        </div>
      )}
    </div>
  );
}
